import React from 'react';
import { type EligibilityCheckResponse, getFinancials } from '@features/patient-coverages/types';
import {
  confirmedPatientAmount,
  eligibilityVisitContext,
  fixedCopayAmount,
  formatUsdCents,
  safeEligibility,
  UNKNOWN_PATIENT_COST,
} from '@features/patient-coverages/utils/patientCost';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';

import { ELIG_TOKENS } from '../theme/readinessTokens';
import { formatSourceProvenance } from '../utils/provenance';

export const EligibilityBenefitsView = ({ data }: { data: EligibilityCheckResponse }) => {
  const fin = getFinancials(data);
  const confirmed = confirmedPatientAmount(data.patient_cost, eligibilityVisitContext(data));

  const copay = fixedCopayAmount(
    safeEligibility(data).patient_cost,
    data.patient_insurance_coverage_id,
  );

  const provenance = formatSourceProvenance(data.source);

  return (
    <DetailSection title="Benefits" provenance={provenance}>
      <Typography variant="body1" sx={{ color: ELIG_TOKENS.ink, fontWeight: 600, mb: 1 }}>
        {confirmed !== undefined
          ? `Confirmed patient amount ${formatUsdCents(confirmed)}`
          : copay !== undefined
            ? copay > 0
              ? `Collect ${formatUsdCents(copay)} co-pay`
              : 'Co-pay $0'
            : UNKNOWN_PATIENT_COST}
      </Typography>
      {copay !== undefined && confirmed === undefined && fin.coinsurance != null && (
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.inkSoft, mb: 1 }}>
          {fin.coinsurance}% coinsurance: dollar amount not determined. Total visit amount not
          determined.
        </Typography>
      )}
      {fin.deductible != null && fin.remaining_deductible != null && fin.deductible > 0 && (
        <Meter
          label="Deductible"
          used={fin.deductible - fin.remaining_deductible}
          total={fin.deductible}
        />
      )}
      {fin.oop_max != null && fin.remaining_oop_max != null && fin.oop_max > 0 && (
        <Meter
          label="Out-of-pocket max"
          used={fin.oop_max - fin.remaining_oop_max}
          total={fin.oop_max}
        />
      )}
      <Stack spacing={0} sx={{ mt: 1 }}>
        {fin.copayment != null && <Field label="Co-pay" value={dollars(fin.copayment)} />}
        {fin.coinsurance != null && <Field label="Coinsurance" value={`${fin.coinsurance}%`} />}
      </Stack>
    </DetailSection>
  );
};

// ─── Internal pieces (also used by EligibilityCoverageBlock) ────────────────

export const DetailSection: React.FC<{
  title: string;
  /** Sits inline after the title text; the title sets the row height, so nothing moves when it is absent. */
  titleControl?: React.ReactNode;
  provenance?: string | null;
  children: React.ReactNode;
}> = ({ title, titleControl, provenance, children }) => (
  <Box sx={{ mb: 3 }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        mb: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="overline" sx={{ color: ELIG_TOKENS.midnight, fontWeight: 600 }}>
          {title}
        </Typography>
        {titleControl}
      </Box>
      {provenance && (
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
          {provenance}
        </Typography>
      )}
    </Box>
    {children}
  </Box>
);

export const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
    <Typography variant="body2" sx={{ color: ELIG_TOKENS.inkSoft }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 500 }}>
      {value}
    </Typography>
  </Box>
);

const Meter: React.FC<{ label: string; used: number; total: number }> = ({
  label,
  used,
  total,
}) => {
  const pct = total > 0 ? Math.max(0, Math.min(100, (used / total) * 100)) : 0;
  return (
    <Box sx={{ py: 0.75 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkSoft }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.ink, fontWeight: 500 }}>
          {dollars(used)} of {dollars(total)} met
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: ELIG_TOKENS.line,
          '& .MuiLinearProgress-bar': { bgcolor: ELIG_TOKENS.midnight },
        }}
      />
    </Box>
  );
};

function dollars(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value % 1 === 0) return `$${value}`;
  return `$${value.toFixed(2)}`;
}
